import { useState } from "react"

import { Button, Col, Form, Modal, Row } from "react-bootstrap"
import { FaFileCsv, FaSync } from "react-icons/fa"

import { Filter, Sort } from "../../api/GridRequest"
import { OrderResultsExportCsv } from "../../api/OrderResults/OrderResultsListGet"
import { AccountTypeID } from "../../config/Constants"
import FormatDate from "../../formatters/FormatDate"
import FormatTimestampFileName from "../../formatters/FormatTimestampFileName"
import exportCsv from "../../utils/exportCsv"
import handleAPIError from "../../utils/handleAPIError"
import ActionButton from "../ActionButton"
import FromToDates from "../Reports/FromToDates"

interface ExportSearchParams {
  field: string
  text: string
  sort?: Sort
  filters: Filter[]
}

interface ExportCSVModalProps {
  isPortalAdmin: boolean
  searchParams: ExportSearchParams
  onClose: () => void
}

function ExportCSVModal(props: ExportCSVModalProps) {
  const { isPortalAdmin, searchParams, onClose } = props
  const [generatingCsv, setGeneratingCsv] = useState(false)
  const [from, setFrom] = useState<Date>()
  const [to, setTo] = useState<Date>()
  const [includeAllDates, setIncludeAllDates] = useState(false)
  const [includeArchivedOrders, setIncludeArchivedOrders] = useState(true)
  const [includeActiveOrders, setIncludeActiveOrders] = useState(true)

  const minDate = new Date(new Date().setFullYear(new Date().getFullYear() - 2))
  const accountType = isPortalAdmin ? 0 : AccountTypeID

  const handleCancelClick = () => onClose()

  const handleClearClick = () => {
    setFrom(undefined)
    setTo(undefined)
    setIncludeAllDates(false)
    setIncludeArchivedOrders(true)
    setIncludeActiveOrders(true)
  }

  const handelActiveChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIncludeActiveOrders(event.target.checked)
  }

  const handelArchivedChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIncludeArchivedOrders(event.target.checked)
  }

  const getReportParams = () => ({
    ...searchParams,
    exportDateFrom: FormatDate(from),
    exportDateTo: FormatDate(to),
    includeArchived: includeArchivedOrders && includeActiveOrders,
    complete: includeArchivedOrders && !includeActiveOrders,
  })

  const checkAllDates = (checked: boolean) => {
    setIncludeAllDates(checked)
    setFrom(undefined)
    setTo(undefined)
  }

  const handleGenerateCsvClick = () => {
    setGeneratingCsv(true)
    OrderResultsExportCsv(getReportParams(), accountType).subscribe(
      (results: string) => {
        setGeneratingCsv(false)
        exportCsv(results, FormatTimestampFileName("Tracking", "csv", new Date()))
      },
      (error) => handleAPIError(error, () => setGeneratingCsv(false))
    )
  }

  const isParamsValid = () => (includeArchivedOrders || includeActiveOrders) && (includeAllDates || (!!from && !!to))

  return (
    <Modal backdrop="static" centered show onHide={handleCancelClick}>
      <Modal.Header closeButton>
        <Modal.Title className="text-uppercase">EXPORT CSV OPTIONS:</Modal.Title>
      </Modal.Header>
      <Modal.Body className="pt-3">
        <Form>
          <Row>
            <Col sm={4}>
              <Form.Label htmlFor="include_all_dates">Include All Dates</Form.Label>
            </Col>
            <Col>
              <Form.Check
                id="include_all_dates"
                type="checkbox"
                inline
                className="abc-checkbox abc-checkbox-inline"
                label=""
                value={includeAllDates ? 1 : 0}
                checked={includeAllDates}
                onChange={(e) => checkAllDates(e.target.checked)}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <FromToDates
                disabled={includeAllDates}
                minDate={minDate}
                from={from}
                to={to}
                onChangeFrom={setFrom}
                onChangeTo={setTo}
              />
            </Col>
          </Row>
          <Row>
            <Col sm={4}>
              <Form.Label className="required">Include Orders</Form.Label>
            </Col>
            <Col>
              <Form.Check
                id="include_active"
                type="checkbox"
                className="abc-checkbox abc-checkbox-primary"
                label="Active"
                value={includeActiveOrders ? 1 : 0}
                checked={includeActiveOrders}
                onChange={handelActiveChange}
              />
              <Form.Check
                id="include_archived"
                type="checkbox"
                className="abc-checkbox abc-checkbox-primary"
                label="Archived"
                value={includeArchivedOrders ? 1 : 0}
                checked={includeArchivedOrders}
                onChange={handelArchivedChange}
              />
            </Col>
          </Row>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleCancelClick}>
          Cancel
        </Button>
        <Button variant="secondary" className="mr-auto" onClick={handleClearClick}>
          Clear Filter&nbsp;
          <FaSync />
        </Button>
        <ActionButton
          variant="primary"
          disabled={!isParamsValid()}
          onClick={handleGenerateCsvClick}
          spin={generatingCsv}
          disableOnSpin
        >
          Export CSV&nbsp;
          <FaFileCsv />
        </ActionButton>
      </Modal.Footer>
    </Modal>
  )
}
export default ExportCSVModal
